import { z } from "zod";

/**
 * Feed page contracts (PREC-3635): company-run cards built from persisted tracker runs
 * (`tracker_signal_run`), the tab counts, the jump-to-company dropdown and the run
 * ignore/undo responses. All count fields are `z.coerce.number()` — SQL aggregates arrive
 * as strings.
 */

/**
 * L2 tabs of the Feed page (statuses reuse the list-detail buckets, PREC-3633).
 * `tracked` lists every tracked company of the selected lists as a run card,
 * regardless of lead status (PREC-3694).
 */
export const FEED_TABS = ["new", "review", "assigned", "ignored", "tracked"] as const;

export type FeedTab = (typeof FEED_TABS)[number];

export const feedTabSchema = z.enum(FEED_TABS);

/** Jump-to-company / card ordering. */
export const FEED_SORTS = ["strength", "date"] as const;

export type FeedSort = (typeof FEED_SORTS)[number];

export const feedSortSchema = z.enum(FEED_SORTS);

/**
 * Lead-score filter of the L3 bar — the list page's `PriorityFilterStatus` values
 * (any-match on the tracked leads' scores; no new vocabulary). `UNSURE` is "?", a
 * lead scoring could not judge (PREC-4075).
 */
export const FEED_LEAD_SCORE_FILTERS = [
	"ALL",
	"ALL_WITH_PRIORITY",
	"P1",
	"P2",
	"P3",
	"DQ",
	"UNSURE",
] as const;

export type FeedLeadScoreFilter = (typeof FEED_LEAD_SCORE_FILTERS)[number];

export const feedLeadScoreFilterSchema = z.enum(FEED_LEAD_SCORE_FILTERS);

/**
 * Signal-score filter of the L3 bar — the list page's `SignalFilterStatus` values
 * (any-match on the priorities of the run's signals; no new vocabulary).
 */
export const FEED_SIGNAL_SCORE_FILTERS = [
	"ALL",
	"ALL_WITH_SIGNAL",
	"S1",
	"S2",
	"S3",
	"DQ",
] as const;

export type FeedSignalScoreFilter = (typeof FEED_SIGNAL_SCORE_FILTERS)[number];

export const feedSignalScoreFilterSchema = z.enum(FEED_SIGNAL_SCORE_FILTERS);

/** Enrichment dot of a contact channel (list-detail derivation). */
export const FEED_ENRICHMENT_STATES = ["found", "missing", "enriching"] as const;

export type FeedEnrichmentState = (typeof FEED_ENRICHMENT_STATES)[number];

export const feedEnrichmentStateSchema = z.enum(FEED_ENRICHMENT_STATES);

/** Card spine = strongest relationship across the card's people. */
export const FEED_SPINES = ["campaign", "outreach", "connected", "none"] as const;

export type FeedSpine = (typeof FEED_SPINES)[number];

export const feedSpineSchema = z.enum(FEED_SPINES);

/** Relationship state of one tracked person (drives the dot + the card spine). */
export const feedRelationshipSchema = z.object({
	/** On a campaign (campaignId set) or in the list-detail assigned bucket. */
	inCampaign: z.boolean(),
	/** Prior conversations exist for the prospect. */
	previousOutreach: z.boolean(),
	/** Accepted LinkedIn invite, or an li-connections row with connectedAt. */
	connectedOnLinkedin: z.boolean(),
});

export type FeedRelationship = z.infer<typeof feedRelationshipSchema>;

export const feedPersonEnrichmentSchema = z.object({
	linkedin: feedEnrichmentStateSchema,
	email: feedEnrichmentStateSchema,
	phone: feedEnrichmentStateSchema,
});

export type FeedPersonEnrichment = z.infer<typeof feedPersonEnrichmentSchema>;

/** A lead of the card's company on the selected lists (a list_person, tracker/signal joined by ref). */
export const feedPersonSchema = z.object({
	/** The lead's list_person id — the feed person's stable key (feed is list_person-driven). */
	listPersonId: z.number(),
	/** Tracked-person ref; null when the lead is not (or not yet) a tracked person. */
	trackerPersonId: z.number().nullable(),
	prospectId: z.number().nullable(),
	/**
	 * True for placeholder leads (SELECT / "Person not found") that have no real contact yet —
	 * the card renders the Select-Prospect CTA in the person slot instead of an identity card.
	 */
	needsProspectSelection: z.boolean(),
	name: z.string().nullable(),
	/** Role / job title. */
	role: z.string().nullable(),
	headline: z.string().nullable(),
	location: z.string().nullable(),
	avatarUrl: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	/** Lead score ring: "1" | "2" | "3" | "Disqualified"; null when unscored. */
	score: z.string().nullable(),
	/** Raw AssignStatus value of the listPerson; null when not on the list. */
	assignStatus: z.string().nullable(),
	/** True when AI copywriting drafts already exist (list_person.copywritingStatus = COMPLETED). */
	copywritingCompleted: z.boolean(),
	enrichment: feedPersonEnrichmentSchema,
	relationship: feedRelationshipSchema,
});

export type FeedPerson = z.infer<typeof feedPersonSchema>;

/** One signal row of a card: the run's new signals plus recent prior signals. */
export const feedSignalSchema = z.object({
	signalId: z.number(),
	/** One-liner shown collapsed (the signal title). */
	line: z.string().nullable(),
	title: z.string().nullable(),
	/**
	 * Type-chip label. The backend has no separate kind vocabulary — this is the
	 * `signalCategory` label; the FE derives chip styling from it.
	 */
	kind: z.string().nullable(),
	summary: z.string().nullable(),
	/** Why-box content. */
	reasoning: z.string().nullable(),
	/** "Prio 1" | "Prio 2" | "Prio 3" | "Disqualified" (legacy rows may differ). */
	priority: z.string().nullable(),
	/** "Lead update" | "Company update" | "Industry update" — drives the scope pill. */
	signalCategory: z.string().nullable(),
	/** "linkedin_post" | "web" | "none" — how to render the source chip. */
	sourceType: z.string(),
	sourceUrl: z.string().nullable(),
	publishedDate: z.string().nullable(),
	signalCollectedAt: z.string().nullable(),
	linkedinPostUrn: z.string().nullable(),
	linkedinPost: z.record(z.string(), z.unknown()).nullable(),
	linkedinCommentary: z.string().nullable(),
	/** True when the signal belongs to the card's run (vs. a recent prior signal). */
	belongsToRun: z.boolean(),
});

export type FeedSignal = z.infer<typeof feedSignalSchema>;

/** Presence flags of the company box (LinkedIn / email / website). */
export const feedCompanyEnrichmentSchema = z.object({
	linkedin: z.boolean(),
	email: z.boolean(),
	website: z.boolean(),
});

export type FeedCompanyEnrichment = z.infer<typeof feedCompanyEnrichmentSchema>;

export const feedCompanySchema = z.object({
	trackerOrganizationId: z.number(),
	organizationId: z.number().nullable(),
	name: z.string().nullable(),
	logoUrl: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	websiteUrl: z.string().nullable(),
	location: z.string().nullable(),
	industry: z.string().nullable(),
	employeeCount: z.coerce.number().nullable(),
	enrichment: feedCompanyEnrichmentSchema,
});

export type FeedCompany = z.infer<typeof feedCompanySchema>;

/** One company-run card ("tracking card"). */
export const feedRunCardSchema = z.object({
	/**
	 * `null` for a runless card — a company with qualifying leads but no `tracker_signal_run`
	 * yet (PREC-3584). Such cards have no AI run summary and cannot be "ignored this time"
	 * (ignore keys on a run); the FE hides the ignore action while this is null.
	 */
	runId: z.number().nullable(),
	listId: z.number(),
	listName: z.string().nullable(),
	/** The card's list has at least one copywriting campaign (reach-out menu). */
	listHasCampaign: z.boolean(),
	runAt: z.string(),
	title: z.string().nullable(),
	/** Sentence 1: what happened this run + why it is relevant to us. */
	summaryWhat: z.string().nullable(),
	/** Sentence 2: connection to prior signals; null on the account's first run. */
	summaryRelation: z.string().nullable(),
	strongestPriority: z.string().nullable(),
	newSignalCount: z.coerce.number(),
	ignoredAt: z.string().nullable(),
	/** Strongest relationship across people: campaign > outreach > connected > none. */
	spine: feedSpineSchema,
	company: feedCompanySchema,
	people: z.array(feedPersonSchema),
	signals: z.array(feedSignalSchema),
});

export type FeedRunCard = z.infer<typeof feedRunCardSchema>;

export const feedPageMetaSchema = z.object({
	page: z.coerce.number(),
	limit: z.coerce.number(),
	itemCount: z.coerce.number(),
	pageCount: z.coerce.number(),
	hasPreviousPage: z.boolean(),
	hasNextPage: z.boolean(),
});

export const feedPageResponseSchema = z.object({
	data: z.array(feedRunCardSchema),
	meta: feedPageMetaSchema,
});

export type FeedPageResponse = z.infer<typeof feedPageResponseSchema>;

/**
 * Tab badge counts — companies, not signals. Same buckets as the list-detail page; a
 * company with leads in several buckets counts in each matching tab (any-match).
 */
export const feedCountsSchema = z.object({
	new: z.coerce.number(),
	review: z.coerce.number(),
	assigned: z.coerce.number(),
	ignored: z.coerce.number(),
	trackedProfiles: z.coerce.number(),
});

export type FeedCounts = z.infer<typeof feedCountsSchema>;

export const feedCountsResponseSchema = z.object({
	data: feedCountsSchema,
});

export type FeedCountsResponse = z.infer<typeof feedCountsResponseSchema>;

/** Jump-to-company dropdown entry; `page` is 1-based within the current tab + sort. */
export const feedCompanyJumpEntrySchema = z.object({
	trackerOrganizationId: z.number(),
	/** `null` for a runless company (PREC-3584) — see {@link feedRunCardSchema}. */
	runId: z.number().nullable(),
	name: z.string().nullable(),
	logoUrl: z.string().nullable(),
	/** Strongest signal one-liner of the run + its type label. */
	topSignalLine: z.string().nullable(),
	topSignalKind: z.string().nullable(),
	page: z.coerce.number(),
});

export type FeedCompanyJumpEntry = z.infer<typeof feedCompanyJumpEntrySchema>;

export const feedCompaniesResponseSchema = z.object({
	data: z.array(feedCompanyJumpEntrySchema),
});

export type FeedCompaniesResponse = z.infer<typeof feedCompaniesResponseSchema>;

/** Response of run ignore/undo — the FE applies the fresh counts directly. */
export const feedIgnoreResponseSchema = z.object({
	data: z.object({
		runId: z.number(),
		ignoredAt: z.string().nullable(),
		counts: feedCountsSchema,
	}),
});

export type FeedIgnoreResponse = z.infer<typeof feedIgnoreResponseSchema>;

/** Request params of GET /feed (listIds is JSON-encoded in the query string). */
export const feedPageParamsSchema = z.object({
	listIds: z.array(z.number()).nonempty(),
	tab: feedTabSchema.optional(),
	page: z.coerce.number().int().min(1).optional(),
	limit: z.coerce.number().int().min(1).max(50).optional(),
	search: z.string().optional(),
	/** JSON-encoded in the query string, like `listIds`. */
	priorityFilterStatus: z.array(feedLeadScoreFilterSchema).optional(),
	/** JSON-encoded in the query string, like `listIds`. */
	signalFilterStatus: z.array(feedSignalScoreFilterSchema).optional(),
	sort: feedSortSchema.optional(),
});

export type FeedPageParams = z.infer<typeof feedPageParamsSchema>;

/** Request params of GET /feed/counts — same filters, no tab/sort/pagination. */
export const feedCountsParamsSchema = feedPageParamsSchema.omit({
	tab: true,
	page: true,
	limit: true,
	sort: true,
});

export type FeedCountsParams = z.infer<typeof feedCountsParamsSchema>;

/** Request params of GET /feed/companies — `limit` is the pager page size. */
export const feedCompaniesParamsSchema = feedPageParamsSchema.omit({ page: true });

export type FeedCompaniesParams = z.infer<typeof feedCompaniesParamsSchema>;

/** Body of POST|DELETE /feed/runs/:runId/ignore (the currently selected lists). */
export const feedIgnoreParamsSchema = z.object({
	listIds: z.array(z.number()).optional(),
});

export type FeedIgnoreParams = z.infer<typeof feedIgnoreParamsSchema>;
